// src/components/promos/PillToggleGroup.tsx

type Props<T extends string> = {
  options: { value: T; label: string }[];
  value: Set<T>;
  onChange: (next: Set<T>) => void;
  /** Label for the separate "no restriction" toggle (empty selection = all). */
  allLabel: string;
};

/**
 * Rounded pill multi-select with a selectable "All" pill. "All" means every
 * option is selected: clicking it lights up all pills, and it lights up itself
 * whenever every pill is individually selected. Deselecting any pill drops it.
 * The parent maps "all selected" back to "no restriction".
 */
export default function PillToggleGroup<T extends string>({ options, value, onChange, allLabel }: Props<T>) {
  const allValues = options.map((o) => o.value);
  const isAll = allValues.length > 0 && allValues.every((v) => value.has(v));

  // Toggle: select every pill, or clear them all when already all-selected.
  const toggleAll = () => onChange(isAll ? new Set<T>() : new Set(allValues));
  const toggle = (v: T) => {
    const next = new Set(value);
    next.has(v) ? next.delete(v) : next.add(v);
    onChange(next);
  };

  const pill = "rounded-full border px-3.5 py-1.5 text-sm font-medium transition select-none";
  const on = "border-sky-500 bg-sky-500 text-white shadow-sm";
  const off = "border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50";

  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {/* "All" toggles every pill on/off; lit whenever all pills are selected. */}
      <button
        type="button"
        onClick={toggleAll}
        aria-pressed={isAll}
        className={`${pill} ${isAll ? on : off}`}
      >
        {allLabel}
      </button>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => toggle(o.value)}
          aria-pressed={value.has(o.value)}
          className={`${pill} ${value.has(o.value) ? on : off}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
